import React, { Suspense, lazy, FC } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ScrollToTop } from '@dxb-web-ui-framework/common/components/ScrollToTop';
import { ErrorBoundary } from 'react-error-boundary';
import { AppHelmetProvider } from '@dxb-web-ui-framework/internationalization/contexts/AppHelmet/AppHelmetProvider';
import { GenericObject } from '@dxb-web-ui-framework/common/types';
import { Box } from '@dxb-web-ui-framework/design-system';
import '../../packages/internationalization/src/utils/i18n';
import { ECOMMBXLoader } from '../../packages/content-composer/src/components/Generic/ECOMMBXLoader/ECOMMBXLoader';
import { useLanguage } from '../../packages/internationalization/src/contexts/Internationalization/hooks';

const LandingPage = lazy(
  () => import(/* webpackChunkName: "LandingPageComponent" */ '../../packages/static-content/src/pages/LandingPage'),
);

const SupportPage = lazy(
  () => import(/* webpackChunkName: "SupportPageComponent" */ '../../packages/static-content/src/pages/SupportPage'),
);

const SecurityTipsPage = lazy(
  () =>
    import(
      /* webpackChunkName: "SecurityTipsPageComponent" */ '../../packages/static-content/src/pages/SecurityTipsPage'
    ),
);

const OnboardingPage = lazy(
  () =>
    import(
      /* webpackChunkName: "OnboardingPageComponent" */ '../../packages/kyc/src/pages/OnboardingPage/OnboardingPage'
    ),
);

const EmailVerificationPage = lazy(
  () =>
    import(
      /* webpackChunkName: "EmailVerificationPageComponent" */ '../../packages/static-content/src/pages/EmailVerificationPage'
    ),
);

const ErrorPage = lazy(
  () => import(/* webpackChunkName: "ErrorPageComponent" */ '../../packages/content-composer/src/pages/ErrorPage'),
);

const SuccessPage = lazy(
  () => import(/* webpackChunkName: "SuccessPageComponent" */ '../../packages/content-composer/src/pages/SuccessPage'),
);

const App: FC<GenericObject> = ({ brandConfig }) => {
  const language = useLanguage();

  return (
    <Box data-testid='app' data-version={process.env.REACT_APP_VERSION}>
      <Router basename={brandConfig.baseName}>
        <ScrollToTop />
        <AppHelmetProvider locale={language?.cultureName}>
          <Suspense fallback={<ECOMMBXLoader size={'large'} />}>
            <ErrorBoundary FallbackComponent={error => <ErrorPage pageConfig={brandConfig.errorPage} />}>
              <Routes>
                <Route index element={<LandingPage pageConfig={brandConfig.landingPage} />} />
                <Route path={'support'} element={<SupportPage pageConfig={brandConfig.supportPage} />} />
                <Route
                  path={'security-tips'}
                  element={<SecurityTipsPage pageConfig={brandConfig.securityTipsPage} />}
                />
                <Route path={'onboarding'} element={<OnboardingPage pageConfig={brandConfig.onboardingPage} />} />
                <Route
                  path={'email-verification'}
                  element={<EmailVerificationPage pageConfig={brandConfig.emailVerificationPage} />}
                />
                <Route path='error' element={<ErrorPage pageConfig={brandConfig.errorPage} />} />
                <Route path='success' element={<SuccessPage pageConfig={brandConfig.successPage} />} />
                <Route path='*' element={<ErrorPage pageConfig={brandConfig.errorPage} />} />
              </Routes>
            </ErrorBoundary>
          </Suspense>
        </AppHelmetProvider>
      </Router>
    </Box>
  );
};

export { App };
